import React, { ReactNode, useEffect, useState } from "react";
import * as FaIcons from "react-icons/fa";
import { MenuItem } from "../../types";
import { Skeleton } from "../skeletons";
import { getAppStyles } from "../../theme/app-theme";

interface MenuSectionProps {
    titulo: string;
    items: MenuItem[];
    color: string;
    borderColor: string;
    icon: ReactNode;
    hover: string;
    press: string;
    isDarkMode?: boolean;
}

const MenuSection: React.FC<MenuSectionProps> = ({
    titulo,
    items,
    color,
    borderColor,
    icon,
    hover,
    press,
    isDarkMode = false,
}) => {
    const [icons, setIcons] = useState<
        Record<string, { Component?: React.FC<React.SVGProps<SVGSVGElement>>; src?: string }>
    >({});
    const [iconsReady, setIconsReady] = useState(false);
    const themeStyles = getAppStyles(isDarkMode);
    const idleCardBg = themeStyles.colors.cardBg;
    const idleCardText = themeStyles.colors.cardText;
    const idleIconColor = themeStyles.colors.cardIcon;

    useEffect(() => {
        let cancelled = false;
        setIconsReady(false);

        (async () => {
            const loadedIcons: Record<
                string,
                { Component?: React.FC<React.SVGProps<SVGSVGElement>>; src?: string }
            > = {};

            for (const item of items) {
                try {
                    const iconModule = await import(
                        /* @vite-ignore */
                        `../../assets/icons/fontawesome/svgs/regular/${item.icone}`
                    );

                    if (iconModule.ReactComponent) {
                        loadedIcons[item.icone] = { Component: iconModule.ReactComponent };
                    } else {
                        loadedIcons[item.icone] = { src: iconModule.default };
                    }
                } catch (err) {
                    console.warn(`❌ Ícone não encontrado: ${item.icone}`);
                }
            }

            if (!cancelled) {
                setIcons(loadedIcons);
                setIconsReady(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [items]);

    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [pressedIndex, setPressedIndex] = useState<number | null>(null);

    const iconFilterByColor = (hex: string) => {
        switch (hex.toUpperCase()) {
            case "#ED1C24":
                return "invert(14%) sepia(88%) saturate(6500%) hue-rotate(350deg)";
            case "#4190C1":
                return "invert(39%) sepia(85%) saturate(2800%) hue-rotate(190deg)";
            case "#009535":
                return "invert(42%) sepia(75%) saturate(2200%) hue-rotate(100deg)";
            default:
                return "invert(36%)";
        }
    };

    const idleIconFilter = isDarkMode
        ? "brightness(0) saturate(100%) invert(75%)"
        : "brightness(0) saturate(100%) invert(36%)";

    const nomeSkeletonWidth = (nome: string) =>
        Math.min(180, Math.max(96, Math.round(nome.length * 7.2)));

    return (
        <section style={{ ...themeStyles.section, border: `1px solid ${borderColor}` }}>
            <h3 style={{ ...styles.title, backgroundColor: color }}>
                {titulo}
                {icon}
            </h3>

            <div style={styles.grid}>
                {items.map((item, index) => {
                    if (!iconsReady) {
                        return (
                            <div
                                key={index}
                                style={{
                                    ...themeStyles.card,
                                    border: `1px solid ${borderColor}`,
                                    pointerEvents: "none",
                                }}
                                aria-busy="true"
                                aria-label={`Carregando ${item.nome}`}
                            >
                                <div style={styles.icon}>
                                    <Skeleton height={28} width={28} borderRadius={6} />
                                </div>
                                <Skeleton
                                    height={14}
                                    width={nomeSkeletonWidth(item.nome)}
                                    borderRadius={4}
                                />
                            </div>
                        );
                    }

                    const IconInfo = icons[item.icone];
                    const IconFallback = (FaIcons as any)[item.icone] || FaIcons.FaRegFileAlt;

                    const isHovered = hoveredIndex === index;
                    const isPressed = pressedIndex === index;

                    const backgroundColor = isPressed ? press : isHovered ? hover : idleCardBg;

                    return (
                        <a
                            key={index}
                            href={item.href}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => {
                                if (hoveredIndex === index) setHoveredIndex(null);
                                if (pressedIndex === index) setPressedIndex(null);
                            }}
                            onMouseDown={() => setPressedIndex(index)}
                            onMouseUp={() => setPressedIndex(null)}
                            style={{
                                ...themeStyles.card,
                                border: `1px solid ${borderColor}`,
                                backgroundColor,
                                transform: isHovered ? "translateY(-3px)" : "translateY(0)",
                                boxShadow: isHovered
                                    ? isDarkMode
                                        ? "0 3px 8px rgba(0, 0, 0, 0.45)"
                                        : "0 3px 8px rgba(0, 0, 0, 0.15)"
                                    : themeStyles.colors.cardShadow,
                            }}
                        >
                            <div style={styles.icon}>
                                {IconInfo?.Component ? (
                                    <IconInfo.Component
                                        width={28}
                                        height={28}
                                        fill={isHovered || isPressed ? color : idleIconColor}
                                        style={{ transition: "fill 0.2s" }}
                                    />
                                ) : IconInfo?.src ? (
                                    <img
                                        src={IconInfo.src}
                                        alt={item.nome}
                                        width={24}
                                        height={24}
                                        style={{
                                            filter: isHovered || isPressed
                                                ? iconFilterByColor(color)
                                                : idleIconFilter,
                                            transition: "filter 0.2s",
                                        }}
                                    />
                                ) : (
                                    <IconFallback
                                        size={28}
                                        color={isHovered || isPressed ? color : idleIconColor}
                                        style={{ transition: "color 0.2s" }}
                                    />
                                )}
                            </div>

                            <span
                                style={{
                                    color: isHovered || isPressed ? color : idleCardText,
                                    transition: "color 0.2s",
                                }}
                            >
                                {item.nome}
                            </span>
                        </a>
                    );
                })}
            </div>
        </section>
    );
};

const styles: Record<string, React.CSSProperties> = {
    title: {
        padding: "10px 15px",
        color: "white",
        borderTopLeftRadius: "16px",
        borderTopRightRadius: "16px",
        fontWeight: "bold",
        flexDirection: "row",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        gap: "15px",
        padding: "20px",
        justifyItems: "center",
        maxWidth: "1000px",
        margin: "0 auto",
    },
    icon: {
        fontSize: "24px",
        marginBottom: "8px",
    },
};

export default MenuSection;
